import React, { useState, useEffect } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from "chart.js";
import jsPDF from "jspdf";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default function SoftwareIPValueCalc() {
  const [name, setName] = useState("");
  const [components, setComponents] = useState([]);
  const [rates, setRates] = useState({
    junior: 500,
    mid: 1200,
    senior: 2500
  });
  const [result, setResult] = useState(null);
  const [saved, setSaved] = useState([]);

  // Base logic
  const baseEffort = {
    frontend: 8,
    api: 6,
    model: 4,
    integration: 12
  };

  const complexity = {
    simple: 1,
    medium: 1.5,
    complex: 2.5
  };

  const calcEffort = (count, type, level) => {
    return count * (baseEffort[type] || 5) * (complexity[level] || 1);
  };

  const calculate = () => {
    let totalHours = 0;

    components.forEach(c => {
      totalHours += calcEffort(c.count, c.type, c.complexity);
    });

    const res = {
      totalHours,
      min: totalHours * rates.junior,
      fair: totalHours * rates.mid,
      max: totalHours * rates.senior
    };

    setResult(res);
  };

  // Component handlers
  const addComponent = () => {
    setComponents([
      ...components,
      { type: "frontend", count: 1, complexity: "medium" }
    ]);
  };

  const updateComponent = (i, field, value) => {
    const updated = [...components];
    updated[i][field] =
      field === "count" ? Number(value) : value;
    setComponents(updated);
  };

  const deleteComponent = (i) => {
    setComponents(components.filter((_, index) => index !== i));
  };

  const loadSample = () => {
    setName("Demo SaaS App");
    setComponents([
      { type: "frontend", count: 10, complexity: "medium" },
      { type: "api", count: 20, complexity: "medium" },
      { type: "model", count: 15, complexity: "simple" },
      { type: "integration", count: 5, complexity: "complex" }
    ]);
  };

  // Save locally
  const saveProject = () => {
    const data = { name, components, rates, result };
    const prev = JSON.parse(localStorage.getItem("ip_projects")) || [];
    const updated = [...prev, data];
    localStorage.setItem("ip_projects", JSON.stringify(updated));
    setSaved(updated);
  };

  const loadSaved = () => {
    const data = JSON.parse(localStorage.getItem("ip_projects")) || [];
    setSaved(data);
  };

  // PDF Download
  const downloadPDF = () => {
    const doc = new jsPDF();
    doc.text("IP Valuation Report", 10, 10);
    doc.text(`Project: ${name}`, 10, 20);
    doc.text(`Total Hours: ${result.totalHours}`, 10, 30);
    doc.text(`Min: ₹${result.min}`, 10, 40);
    doc.text(`Fair: ₹${result.fair}`, 10, 50);
    doc.text(`Max: ₹${result.max}`, 10, 60);
    doc.text(
      "Disclaimer: Indicative only. Not a certified valuation.",
      10,
      80
    );
    doc.save("Software IP Valuation Report.pdf");
  };

  useEffect(() => {
    loadSaved();
  }, []);

  // Chart Data
  const chartData = {
    labels: ["Min", "Fair", "Max"],
    datasets: [
      {
        label: "Valuation ₹",
        data: result
          ? [result.min, result.fair, result.max]
          : [0, 0, 0]
      }
    ]
  };

  return (
    <div className="bg-black text-white min-h-screen p-4">
      <h1 className="text-lg mb-4">💻 Software IP Valuation</h1>

      {/* Project Name */}
      <input
        placeholder="Project Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        className="w-full h-12 p-2 mb-3 text-black"
      />

      {/* Buttons */}
      <div className="flex gap-2 mb-3">
        <button onClick={addComponent} className="bg-green-600 px-2 py-1 text-xs lg:text-sm w-20 lg:w-32">
          ➕ Add
        </button>
        <button onClick={loadSample} className="bg-blue-600 px-2 py-1 text-xs lg:text-sm w-20 lg:w-32">
          📂 Sample
        </button>
      </div>

      {/* Components */}
      {components.map((c, i) => (
        <div key={i} className="border p-2 mb-2">
          <select
            value={c.type}
            onChange={(e) =>
              updateComponent(i, "type", e.target.value)
            }
            className="text-black h-12"
          >
            <option value="frontend">Frontend</option>
            <option value="api">API</option>
            <option value="model">Model</option>
            <option value="integration">Integration</option>
          </select>

          <input
            type="number"
            value={c.count}
            onChange={(e) =>
              updateComponent(i, "count", e.target.value)
            }
            className="text-black mx-2 w-16 h-12"
          />

          <select
            value={c.complexity}
            onChange={(e) =>
              updateComponent(i, "complexity", e.target.value)
            }
            className="text-black h-12"
          >
            <option value="simple">Simple</option>
            <option value="medium">Medium</option>
            <option value="complex">Complex</option>
          </select>

          <button
            onClick={() => deleteComponent(i)}
            className="bg-blue-500 text-red-500 ml-2 text-sm lg:text-lg w-12 lg:w-20 h-12"
          >
            ❌
          </button>
        </div>
      ))}

      {/* Rates */}
      <div className="mt-4">
        <p className="text-cyan-200">Rates (₹/hr)</p>
        <input
          type="number"
          value={rates.junior}
          onChange={(e) =>
            setRates({ ...rates, junior: Number(e.target.value) })
          }
          className="text-black w-20 mr-2 h-12"
        />
        <input
          type="number"
          value={rates.mid}
          onChange={(e) =>
            setRates({ ...rates, mid: Number(e.target.value) })
          }
          className="text-black w-20 mr-2 h-12"
        />
        <input
          type="number"
          value={rates.senior}
          onChange={(e) =>
            setRates({ ...rates, senior: Number(e.target.value) })
          }
          className="text-black w-20 h-12"
        />
      </div>

      {/* Calculate */}
      <button
        onClick={calculate}
        className="bg-yellow-500 px-4 py-2 mt-4 text-xs lg:text-sm w-32 lg:w-40"
      >
        ⚡ Calculate
      </button>

      {/* Result */}
      {result && (
        <div className="mt-4 border p-3 [&_p]:text-white">
          <p>Total Hours: {result.totalHours}</p>
          <p>Min: ₹{result.min}</p>
          <p>Fair: ₹{result.fair}</p>
          <p>Max: ₹{result.max}</p>

          {/* <Bar data={chartData} /> */}
          <Bar
            data={chartData}
            options={{
                responsive: true,
                plugins: {
                legend: {
                    labels: {
                    color: "#ffffff", // legend text
                    },
                },
                },
                scales: {
                x: {
                    ticks: {
                    color: "#ffffff", // x-axis labels
                    },
                    grid: {
                    color: "#444", // subtle grid
                    },
                },
                y: {
                    ticks: {
                    color: "#ffffff", // y-axis labels
                    },
                    grid: {
                    color: "#444",
                    },
                },
                },
            }}
           />

          <div className="flex gap-2 mt-3">
            <button onClick={saveProject} className="bg-green-700 px-2 py-1 text-xs lg:text-sm w-20 lg:w-32">
              💾 Save
            </button>
            <button onClick={downloadPDF} className="bg-blue-700 px-2 py-1 text-xs lg:text-sm w-20 lg:w-32">
              📄 PDF
            </button>
          </div>
        </div>
      )}

      {/* Saved */}
      <div className="mt-6">
        <h2>Saved Projects</h2>
        {saved.map((p, i) => (
          <div key={i} className="border p-2 mt-2">
            {p.name} - ₹{p.result?.fair}
          </div>
        ))}
      </div>
    </div>
  );
}